import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { BookCopy, Briefcase, UserPlus, FileCheck, Handshake, LineChart, ArrowRight, Phone, Mail, MessageSquare, User, ExternalLink, MessageCircle, CheckCircle2, Clock } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';

const featuredVietnameseEmployers = [
  { id: 'vinfast', name: 'VinFast', logo: '/img/taitro1.jpg', dataAiHint: 'vinfast logo' },
  { id: 'viettel', name: 'Viettel', logo: '/img/taitro2.jpg', dataAiHint: 'viettel logo' },
  { id: 'fpt', name: 'FPT Software', logo: '/img/taitro3.jpg', dataAiHint: 'fpt logo' },
  { id: 'thaco', name: 'THACO', logo: '/img/taitro4.jpg', dataAiHint: 'thaco logo' },
  { id: 'masan', name: 'Masan Group', logo: '/img/taitro5.jpg', dataAiHint: 'masan logo' },
];

export const AgencyMainContent = () => {
    const { isLoggedIn, user } = useAuth();
    const { language } = useLanguage();
    
    const t = agencyTranslations;

    const agencyFeatures = [
        {
         id: 'intro-worker',
         title: t.benefits.items.intro.title[language],
         category: t.benefits.items.intro.category[language],
         description: t.benefits.items.intro.desc[language],
         image: '/img/giao_tiep.jpg?v=1',
       },
       {
         id: 'account-mgmt',
         title: t.benefits.items.mgmt.title[language],
         category: t.benefits.items.mgmt.category[language],
         description: t.benefits.items.mgmt.desc[language],
         image: '/img/teamwork.jpg?v=1',
       },
       {
         id: 'direct-share',
         title: t.benefits.items.share.title[language],
         category: t.benefits.items.share.category[language],
         description: t.benefits.items.share.desc[language],
         image: '/img/an_toan_lao_dong.jpg?v=1',
       },
       {
         id: 'track-progress',
         title: t.benefits.items.track.title[language],
         category: t.benefits.items.track.category[language],
         description: t.benefits.items.track.desc[language],
         image: '/img/e_learning.jpg',
       },
    ];

    const renderRegistrationButton = () => {
        if (!isLoggedIn) {
            return (
                <Button asChild size="lg" className="h-12 px-8 rounded-xl bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white font-bold shadow-lg transition-all active:scale-95">
                    <Link href="/doi-tac/quy-trinh">{t.roadmap.registerNow[language]}</Link>
                </Button>
            );
        }

        const status = user?.partnershipStatus;

        if (status === 'APPROVED') {
            return (
                <Button asChild size="lg" className="h-12 px-8 rounded-xl bg-[#AFC536] hover:bg-[#AFC536]/90 text-white font-bold shadow-lg gap-2 transition-all active:scale-95">
                    <Link href="/doi-tac/viec-lam">
                        <CheckCircle2 className="h-5 w-5" />
                        {t.roadmap.registered[language]}
                    </Link>
                </Button>
            );
        }

        if (status === 'PENDING') {
            return (
                <Button asChild size="lg" className="h-12 px-8 rounded-xl bg-[#F2B92A]/10 text-[#F2B92A] hover:bg-[#F2B92A]/20 font-bold border border-[#F2B92A]/30 gap-2 transition-all active:scale-95">
                    <Link href="/doi-tac/viec-lam">
                        <Clock className="h-5 w-5 animate-pulse" />
                        {t.roadmap.pending[language]}
                    </Link>
                </Button>
            );
        }

        return (
            <Button asChild size="lg" className="h-12 px-8 rounded-xl bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white font-bold shadow-lg transition-all active:scale-95">
                <Link href="/doi-tac/quy-trinh">{t.roadmap.registerNow[language]}</Link>
            </Button>
        );
    };

    return (
    <>
      {/* 3 Step Process */}
      <section className="w-full pt-20 md:pt-28 bg-background">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-center mb-16">
            {t.process.title[language]}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center shadow-lg hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-2">
              <CardHeader>
                <div className="mx-auto bg-orange-100 rounded-full p-4 w-fit">
                  <UserPlus className="h-10 w-10 text-[#F2B92A]" />
                </div>
                <CardTitle className="font-headline mt-4">{t.process.step1.title[language]}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {t.process.step1.desc[language]}
                </p>
              </CardContent>
            </Card>
            <Card className="text-center shadow-lg hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-2">
              <CardHeader>
                <div className="mx-auto bg-yellow-100 rounded-full p-4 w-fit">
                  <FileCheck className="h-10 w-10 text-[#F2B92A]" />
                </div>
                <CardTitle className="font-headline mt-4">{t.process.step2.title[language]}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {t.process.step2.desc[language]}
                </p>
              </CardContent>
            </Card>
            <Card className="text-center shadow-lg hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-2">
              <CardHeader>
                <div className="mx-auto bg-orange-50 rounded-full p-4 w-fit">
                  <Handshake className="h-10 w-10 text-[#F2B92A]" />
                </div>
                <CardTitle className="font-headline mt-4">{t.process.step3.title[language]}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {t.process.step3.desc[language]}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="w-full py-20 md:py-28 bg-accent/20">
        <div className="container mx-auto px-4 md:px-6">
          <Card className="overflow-hidden shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 items-center">
              <div className="relative aspect-[16/10] md:aspect-auto md:h-full order-last md:order-first">
                <Image 
                  src="/img/BANGLADESH/TRANGVIETNAM/doi-tac-home.jpg"
                  alt={t.alt.partners[language]}
                  fill
                  className="object-cover"
                />
              </div>
              <div className="p-8 md:p-12 text-center md:text-left">
                <h2 className="text-3xl md:text-4xl font-headline font-bold text-[#F2B92A] mb-4">
                  {t.roadmap.title[language]}
                </h2>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto md:mx-0">
                  {t.roadmap.desc[language]}
                </p>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                    {renderRegistrationButton()}
                    <Button 
                        asChild
                        size="lg" 
                        variant="outline" 
                        className="h-12 px-8 rounded-xl border-[#F2B92A] text-[#F2B92A] hover:bg-[#F2B92A]/10 font-bold transition-all active:scale-95"
                    >
                        <Link href="/doi-tac/viec-lam">
                            {t.roadmap.consultation[language]}
                        </Link>
                    </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Vietnamese Employers */}
      <section className="w-full py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4 md:px-6">
           <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-headline font-bold">{t.partners.title[language]}</h2>
            <p className="text-lg text-muted-foreground mt-4 max-w-3xl mx-auto">
              {t.partners.desc[language]}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-stretch">
            {featuredVietnameseEmployers.map(emp => (
              <div key={emp.id} className="flex justify-center items-center p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
                <Image src={emp.logo} alt={emp.name} width={150} height={50} className="grayscale hover:grayscale-0 transition-all duration-300"/>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features & Tools */}
      <section className="w-full py-20 md:py-28 bg-secondary">
        <div className="container mx-auto px-4 md:px-6">
           <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-[#F2B92A]">{t.benefits.title[language]}</h2>
            <p className="text-lg text-muted-foreground mt-4 max-w-3xl mx-auto">
              {t.benefits.desc[language]}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {agencyFeatures.map(feature => (
              <Card key={feature.id} className="flex flex-col overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group">
                <CardHeader className="p-0">
                    <Image
                      src={feature.image}
                      alt={feature.title}
                      width={600}
                      height={400}
                      className="w-full h-48 object-cover"
                    />
                </CardHeader>
                <CardContent className="p-6 flex-grow">
                  <p className="text-sm font-bold mb-2 text-[#F2B92A]">{feature.category}</p>
                  <CardTitle className="font-headline text-xl mb-2 group-hover:text-[#F2B92A] transition-colors">{feature.title}</CardTitle>
                  <p className="text-sm text-muted-foreground line-clamp-3">{feature.description}</p>
                </CardContent>
                <CardFooter className="p-6 pt-0 mt-auto">
                   <div className="font-bold text-[#F2B92A] flex items-center cursor-pointer hover:underline">
                    {t.benefits.learnMore[language]} <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center gap-12 rounded-3xl bg-gradient-to-br from-[#F2B92A] to-[#F2B92A] text-white p-12 lg:p-16 shadow-xl">
            <div className="md:w-1/2">
              <h2 className="text-3xl md:text-4xl font-headline font-bold mb-4">{t.cta.title[language]}</h2>
              <p className="text-lg text-white/90 mb-8">
                {t.cta.desc[language]}
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <Button asChild size="lg" className="h-12 px-8 rounded-xl bg-white text-[#F2B92A] hover:bg-white/90 font-bold border-none shadow-md transition-all active:scale-95">
                  <Link href="/doi-tac/viec-lam">{t.cta.manageJobs[language]}</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-12 px-8 rounded-xl bg-transparent text-white border-white hover:bg-white/10 font-bold transition-all active:scale-95">
                  <Link href="/doi-tac/ung-vien">{t.cta.manageCandidates[language]}</Link>
                </Button>
              </div>
            </div>
             <div className="md:w-1/2 flex justify-center">
              <Image
                src="/img/BANGLADESH/TRANGVIETNAM/doi-tac-chibi.jpg"
                alt={t.alt.cooperation[language]}
                width={500}
                height={350}
                className="rounded-2xl shadow-2xl border-4 border-white/20 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

    </>
  );
};
